// src/pages/Wallet/components/NFTItem.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './NFTItem.css';

const NFTItem = ({ nftObj, onClick }) => {
  const { t } = useTranslation();
  const { nft } = nftObj;
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasImageSrc, setHasImageSrc] = useState(false);
  
  // Determine image source
  const getImageSrc = () => {
    if (imageError) return null;
    return nft.imageUrl || nft.image;
  };

  // Check if we have an image source and reset states when it changes
  useEffect(() => {
    const imageSrc = getImageSrc();
    const hasImage = !!(imageSrc && imageSrc.trim());
    
    setHasImageSrc(hasImage);
    setImageLoaded(false); // Reset loaded state when image source changes
    setImageError(false); // Reset error state when image source changes
  }, [nft.imageUrl, nft.image]);
  
  // Handle image load error
  const handleImageError = (e) => {
    console.log('NFT Image load error for:', nft.contractAddress, nft.tokenId, e.target.src);
    setImageError(true);
    setImageLoaded(false);
  };
  
  // Handle image load success
  const handleImageLoad = (e) => {
    console.log('NFT Image loaded successfully for:', nft.contractAddress, nft.tokenId, e.target.src);
    setImageLoaded(true);
    setImageError(false);
  };
  
  // Get display name for the NFT
  const getDisplayName = () => {
    if (nft.name && nft.name !== nft.collectionName) {
      return nft.name;
    }
    if (nft.collectionName) {
      return `${nft.collectionName} #${nft.tokenId}`;
    }
    return `Token #${nft.tokenId}`;
  };
  
  // Get collection name
  const getCollectionName = () => {
    return nft.collectionName || t('wallet.nft.unknownCollection', 'Unknown Collection');
  };
  
  // Get additional info for virtual tokens
  const getAdditionalInfo = () => {
    if (nft.virtualTokenId && nft.collectionCount > 1) {
      return t('wallet.nft.partOfCollection', '{{current}} of {{total}}', { 
        current: nft.tokenId, 
        total: nft.collectionCount 
      });
    }
    return null;
  };

  // Determine what to show in the image container
  const renderImageContent = () => {
    const imageSrc = getImageSrc();
    
    // Show fallback if no image source or there was an error
    if (!hasImageSrc || imageError || !imageSrc) {
      return (
        <div className="nft-image-fallback">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21,15 16,10 5,21"/>
          </svg>
          <span className="fallback-text">
            {t('wallet.nft.noImage', 'No Image')}
          </span>
        </div>
      );
    }
    
    // We have an image source, show loading spinner until image loads
    const isCurrentlyLoading = hasImageSrc && !imageLoaded && !imageError;
    
    return (
      <>
        {/* Show loading spinner while image is loading */}
        {isCurrentlyLoading && (
          <div className="nft-image-placeholder">
            <div className="nft-loading-spinner"></div>
          </div>
        )}
        
        {/* Always render the image, but control visibility via CSS */}
        <img
          src={imageSrc}
          alt={getDisplayName()}
          className={`nft-image ${isCurrentlyLoading ? 'loading' : 'loaded'}`}
          onError={handleImageError}
          onLoad={handleImageLoad}
          loading="lazy"
          style={{ 
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      </>
    );
  };
  
  return (
    <div className="nft-item" onClick={() => onClick && onClick(nftObj)}>
      <div className="nft-image-container">
        {renderImageContent()}
      </div>
      
      <div className="nft-info">
        <div className="nft-name" title={getDisplayName()}>
          {getDisplayName()}
        </div>
        <div className="nft-collection" title={getCollectionName()}>
          {getCollectionName()}
        </div>
        <div className="nft-token-id">
          #{nft.tokenId}
          {nft.virtualTokenId && (
            <span className="virtual-indicator">*</span>
          )}
        </div>
        {getAdditionalInfo() && (
          <div className="nft-additional-info">
            {getAdditionalInfo()}
          </div>
        )}
      </div>
    </div>
  );
};

export default NFTItem;